import { useEffect, useId, useRef, type ReactNode } from "react";

// Native <dialog> + showModal(): the browser supplies the focus trap, inert
// background, Escape handling and top-layer stacking, which is more robust
// than a hand-rolled modal. Focus returns to the opener on close.

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** Role "alertdialog" for confirmations of irreversible actions. */
  alert?: boolean;
  /** Prevent Escape/backdrop close (e.g. while a request is in flight). */
  dismissible?: boolean;
}

export function Dialog({ open, onClose, title, description, children, actions, alert, dismissible = true }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<Element | null>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      openerRef.current = document.activeElement;
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    } else if (!open && el.open) {
      if (typeof el.close === "function") el.close();
      else el.removeAttribute("open");
      const opener = openerRef.current;
      if (opener instanceof HTMLElement) opener.focus();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      role={alert ? "alertdialog" : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      {open ? (
        <div className="dialog-inner">
          <h2 id={titleId} style={{ fontSize: "var(--text-h3)" }}>
            {title}
          </h2>
          {description ? (
            <div id={descId} className="muted">
              {description}
            </div>
          ) : null}
          {children}
          {actions ? <div className="dialog-actions">{actions}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}
